import { SignInUrlBuilder } from '@tableau/shared-urls/lib/src/SignInUrlBuilder';
import Axios from 'axios';
import * as moment from 'moment-timezone';
import * as _ from 'underscore';
import { LoginUrlsWrapper } from '~src/app/spaces/Login/LoginUrls';
import { WindowPopupService } from '~src/authentication/services/WindowPopupService';
import { Location } from '~src/common/react/Location';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { StartPageService } from '~src/settings/services/StartPageService';
import { GetNextPasswordValidationAttemptDate, GetNextPasswordValidationAttemptDateResult } from '~src/ts/ServerApi/requests/GetNextPasswordValidationAttemptDate';
import { IResetPasswordParams, ResetPasswordRequest } from '~src/ts/ServerApi/requests/ResetPasswordRequest';
import { IUpdateExpiredPasswordParams, UpdateExpiredPasswordRequest } from '~src/ts/ServerApi/requests/UpdateExpiredPasswordRequest';
import { IValidatePasswordResetTokenParams, IValidatePasswordResetTokenResult, ValidatePasswordResetTokenRequest } from '~src/ts/ServerApi/requests/ValidatePasswordResetTokenRequest';
import { Telemetry } from '../../commonInfra/services/Telemetry';
import { WindowLocationService } from '../../commonInfra/services/WindowLocationService';
import { Request } from '../../httpClient/serverApi/requests/Request';
import WebHttpClient from '../../httpClient/WebHttpClient';
import {
  ErrorReason,
  getSessionManagementServiceInstance
} from '../../sessionManagement/SessionManagementService';
import { ServerSettingsUnauthenticated } from '../../settings/services/ServerSettingsUnauthenticated';
import { AuthenticationType } from '../../settings/types/IServerSettingsUnauthenticated';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { LoginErrorCodes } from '../../ts/helpers/ErrorCodes';
import { ClientCertificateLoginRequest } from '../../ts/ServerApi/requests/ClientCertificateLoginRequest';
import { CreatePersonalAccessTokenRequest, CreatePersonalAccessTokenResult } from '../../ts/ServerApi/requests/CreatePersonalAccessTokenRequest';
import { GeneratePublicKeyRequest, IGeneratePublicKeyResult } from '../../ts/ServerApi/requests/GeneratePublicKeyRequest';
import { IGetSessionInfoResult } from '../../ts/ServerApi/requests/GetSessionInfoRequest';
import { GetSiteSamlLoginInfoRequest } from '../../ts/ServerApi/requests/GetSiteSamlLoginInfoRequest';
import { KerberosLoginRequest } from '../../ts/ServerApi/requests/kerberosLoginRequest';
import { LoginRequest } from '../../ts/ServerApi/requests/LoginRequest';
import { ILogoutResult, LogoutRequest } from '../../ts/ServerApi/requests/LogoutRequest';
import { SspiLoginRequest } from '../../ts/ServerApi/requests/SspiLoginRequest';
import { IGetSiteSamlLoginInfoResult } from '../../ts/ServerApi/types/IGetSiteSamlLoginInfoResult';
import { ILoginParams } from '../../ts/ServerApi/types/ILoginParams';
import { ISessionInfo } from '../../ts/ServerApi/types/ISessionInfo';
import { ISiteLoginParams } from '../../ts/ServerApi/types/ISiteLoginParams';
import { AuthenticationTypeResolutionService } from './AuthenticationTypeResolutionService';

export namespace AuthenticationService {

  // Yes, the name of the default site is an empty-string.
  export const DEFAULT_SITE_URL_NAMESPACE = '';

  export function login(username: string, password: string, signinQueryParams: SignInUrlBuilder.QueryParams): Promise<void> {
    return getSessionManagementServiceInstance().sessionInfoRequest(() => {
      return WebHttpClient.requestPost(new GeneratePublicKeyRequest())
        .then((generatePublicKeyResult: IGeneratePublicKeyResult) => {
          const loginParams: ILoginParams = {
            username: username,
            encryptedPassword: rsa.encrypt(password, generatePublicKeyResult.key),
            keyId: generatePublicKeyResult.keyId,
          };
          if (isSigninToSpecificSite(signinQueryParams)) {
            loginParams.siteUrlName = getSiteName(signinQueryParams);
          }
          const loginRequest = new LoginRequest(loginParams);
          return WebHttpClient.requestPost(loginRequest)
            .then((loginResult: ISessionInfo) => {
              if (loginResult.errors) {
                return Promise.reject(loginResult.errors[0]);
              }
              if (FeatureFlags.intlTelemetry) {
                const user_role = (loginResult.site.role);
                Telemetry.sendEvent({
                  'type': 'i18n_user_setting',
                  'Product': 'server_client',
                  'Action': 'login',
                  'User_Role': loginResult.site.role,
                  'Language': loginResult.user.language,
                  'Locale': loginResult.user.locale,
                  'Browser_Language': navigator.language,
                  'Browser_Type': navigator.userAgent,
                });
              }
              return getSessionManagementServiceInstance().applySessionInfoInClient(loginResult);
            });
        });
    });
  }

  export function getNextLocalAuthLoginAttempt(username: string): Promise<Date> {
    return WebHttpClient.sendRequest(new GetNextPasswordValidationAttemptDate(username)).then(
      (result: GetNextPasswordValidationAttemptDateResult) => {
        if (result.errors) {
          return Promise.reject(result.errors[0]);
        }
        return Promise.resolve(moment(<any>result).toDate());
      }
    );
  }

  export function siteSamlLogin(username: string): Promise<IGetSiteSamlLoginInfoResult> {
    const loginParams: ILoginParams = {
      username: username,
      encryptedPassword: null,
      keyId: null,
    };
    return WebHttpClient.requestPost(new GetSiteSamlLoginInfoRequest(loginParams))
      .then<IGetSiteSamlLoginInfoResult>((result: IGetSiteSamlLoginInfoResult) => {
        if (result && result.errors) {
          // API calls return status code 200 and then an error in the body when an exception is raised.
          return Promise.reject(result.errors[0].code);
        } else {
          // Result will either be NULL for users configured with server default auth,
          // or an instace of LoginInfoResult for users configured for site-specific saml.
          return Promise.resolve(result);
        }
      });
  }

  export function autoLogin(signinQueryParams?: SignInUrlBuilder.QueryParams): Promise<void> {
    return getSessionManagementServiceInstance().sessionInfoRequest(() => {
      const loginParams: ISiteLoginParams = {};
      if (signinQueryParams && isSigninToSpecificSite(signinQueryParams)) {
        loginParams.siteUrlName = getSiteName(signinQueryParams);
      }
      const authType = AuthenticationTypeResolutionService.authenticationType();
      let loginRequest: Request<ISiteLoginParams, IGetSessionInfoResult>;
      switch (authType) {
        case AuthenticationType.Sspi:
          loginRequest = new SspiLoginRequest(loginParams);
          break;
        case AuthenticationType.Kerberos:
          loginRequest = new KerberosLoginRequest(loginParams);
          break;
        case AuthenticationType.ClientCertificate:
        case AuthenticationType.ClientCertificateWithFallback:
          loginRequest = new ClientCertificateLoginRequest(loginParams);
          break;
      }
      // This must be GET to get around an IE "feature". See B144390
      return WebHttpClient.requestGet(loginRequest)
        .then((sessionInfo: ISessionInfo) => {
          if (sessionInfo.errors) {
            return Promise.reject(sessionInfo.errors[0] && sessionInfo.errors[0].code);
          }
          return getSessionManagementServiceInstance().applySessionInfoInClient(sessionInfo);
        }, (reason: number) => {
          getSessionManagementServiceInstance().clearSessionInfo();
          if (reason === ErrorReason.Unauthorized || reason === ErrorReason.Unknown) {
            switch (authType) {
              case AuthenticationType.Sspi:
                reason = LoginErrorCodes.SspiFailed;
                break;
              case AuthenticationType.Kerberos:
                reason = LoginErrorCodes.KerberosFailed;
                break;
              case AuthenticationType.ClientCertificate:
              case AuthenticationType.ClientCertificateWithFallback:
                reason = LoginErrorCodes.ClientCertificateNotFound;
                break;
            }
          }
          return Promise.reject(reason);
        });
    });
  }

  /**
   * IMPORTANT: This method will return "" (empty string) as a valid value of a target site.
   *            That's the name of the Default site.
   *            It will return NULL if there is no target site.
   */
  export function getSiteName(signinQueryParams: SignInUrlBuilder.QueryParams): string | null {
    // The site argument can be present, but be empty-string, because that's the name of the Default site.
    // The querystring would look like this: http://localhost/#/signin?target_site=&format=xml.
    if (_.isString(signinQueryParams.site)) {
      return signinQueryParams.site;
    } else if (_.isString(signinQueryParams.target_site)) {
      return signinQueryParams.target_site;
    }
    return null;
  }

  export function isSigninToSpecificSite(signinQueryParams: SignInUrlBuilder.QueryParams): boolean {
    return getSiteName(signinQueryParams) !== null;
  }

  export function redirectAfterSuccessfulLogin(signinQueryParams: SignInUrlBuilder.QueryParams) {
    // If we are in a popup window, inform that we've finished the login flow.
    if (WindowPopupService.maybeNotifyAndClose()) {
      return;
    }

    // If we're not on a popup, redirect user to the appropriate place.
    const redirect = signinQueryParams.redirect;
    const externalRedirect = signinQueryParams.externalRedirect;

    if (externalRedirect) {
      // External: does not require hash prefix
      WindowLocationService.setPathQueryFragment(externalRedirect);
    } else if (redirect) {
      Location.instance.setHash(redirect);
    } else if (getSessionManagementServiceInstance().isSessionMultiSite() && !isSigninToSpecificSite(signinQueryParams)) {
      // If this is a multi-site session and the user did not specify a url,
      // let the user choose which site to enter.
      LoginUrlsWrapper.navigateToLoginSitePicker();
    } else {
      Location.instance.setHash(StartPageService.getCurrentUserStartPageForCurrentSite());
    }
  }

  export function signOut() {
    WebHttpClient.requestPost(new LogoutRequest())
      .then((logoutResult: ILogoutResult) => {
        if (ServerSettingsUnauthenticated.getAuthenticationType() === AuthenticationType.Saml &&
          isSAMLLogoutEndpoint(logoutResult.redirectUrl)) {
          // For Server-Wide SAML, we will do a special bit of logic.
          performSAMLLogout(logoutResult);
        } else {
          // This causes all scripts to reload, so all client side app state will be cleared.
          WindowLocationService.navigateTo(logoutResult.redirectUrl || '/');
        }
      });
  }

  /**
   * We have a special endpoint for the server-wide SAML SingleLogout scenario that performs the logout logic
   * instead of the standard logout request.
   */
  function isSAMLLogoutEndpoint(targetRelativeURL: string) {
    // The Spring Filter is registered against this URL.
    return targetRelativeURL.includes('/saml/logout');
  }

  /**
   * To do a SAML Logout, the initial logout returns us the SAML Single-Logout URL.
   * That endpoint will perform logout and return an HTML page that onload makes a POST request to
   * the configured IdP to cascade the logout. (This is a standard SAML POST-Binding).
   * The IdP will eventually redirect the user back to our configured logout landing page.
   */
  function performSAMLLogout(logoutResult: ILogoutResult) {
    // Note: We can't use a browser redirect or browser form action to perform this because we need to
    //       include the XSRF header in the request! So the alternative is to send the post through Ajax
    //       and load the response html onto the page. TFSID: 735765

    // Bypassing AxiosHttpClient because we are special and we need the raw response, this is not a standard webclient api 
    // this is SAML POST binding flow.
    Axios({
      method: 'POST',
      url: logoutResult.redirectUrl,
      data: {},
    }).then((successResponse: any) => {
      if (successResponse && successResponse.data) {
        document.open();
        document.write(successResponse.data);
        document.close();
      }
    });
  }

  export function createPersonalAccessToken(tokenName: string): Promise<CreatePersonalAccessTokenResult> {
    return WebHttpClient.sendRequest(new CreatePersonalAccessTokenRequest({
      clientId: tokenName
    }));
  }

  export function sendPasswordResetRequest(username: string, oldPassword: string, newPassword: string, token: string): Promise<IResult> {
    return WebHttpClient.sendRequest(new GeneratePublicKeyRequest())
      .then((generatePublicKeyResult: IGeneratePublicKeyResult) => {
        return WebHttpClient.sendRequest(<any>getPasswordResetRequest(generatePublicKeyResult, username, oldPassword, newPassword, token))
          .then((resetPasswordResult: IResult) => {
            if (resetPasswordResult.errors) {
              return Promise.reject(resetPasswordResult.errors[0]);
            } else {
              return Promise.resolve(resetPasswordResult);
            }
          }
          );
      });
  }

  export function validatePasswordResetToken(token: string): Promise<IValidatePasswordResetTokenResult> {
    const requestParams: IValidatePasswordResetTokenParams = {
      token: token
    };

    const validateTokenRequest = new ValidatePasswordResetTokenRequest(requestParams);
    return WebHttpClient.sendRequest(validateTokenRequest)
      .then((validateTokenResult: IValidatePasswordResetTokenResult) => {
        if (validateTokenResult.errors) {
          return Promise.reject(validateTokenResult.errors[0].code);
        } else {
          return Promise.resolve(validateTokenResult);
        }
      });
  }

  function getPasswordResetRequest(publicKey: IGeneratePublicKeyResult, username: string, oldPassword: string, newPassword: string, token: string):
    ResetPasswordRequest | UpdateExpiredPasswordRequest {
    if (token !== '') { //token request

      const resetPasswordParams: IResetPasswordParams = {
        resetToken: token,
        newEncryptedPassword: rsa.encrypt(newPassword, publicKey.key),
        encryptionKeyId: publicKey.keyId
      };
      return new ResetPasswordRequest(resetPasswordParams);

    } else {
      const updatedPasswordParams: IUpdateExpiredPasswordParams = {
        username: username,
        oldEncryptedPassword: rsa.encrypt(oldPassword, publicKey.key),
        newEncryptedPassword: rsa.encrypt(newPassword, publicKey.key),
        encryptionKeyId: publicKey.keyId
      };
      return new UpdateExpiredPasswordRequest(updatedPasswordParams);
    }
  }
}

export type AuthenticationService = typeof AuthenticationService;

